import { compositeOnWhite, parseCssColorToRgb, rgbToHex } from './colorDistance';
import { formatHexColor } from './editorUnits';
import { setElementPaintColor } from './svgColorApply';

const OPACITY_ATTRS = [
  'opacity',
  'fill-opacity',
  'stroke-opacity',
  'stop-opacity',
  'flood-opacity',
  'mask-opacity',
] as const;

const OPACITY_STYLE_PROP = /^(?:-webkit-)?(?:opacity|fill-opacity|stroke-opacity|stop-opacity|flood-opacity|mask-opacity)$/i;

function readStyleProperty(style: string, name: string): string | null {
  const match = style.match(new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() ?? null;
}

function cleanOpacityFromStyle(style: string): string {
  return style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const prop = part.split(':')[0]?.trim() || '';
      return !OPACITY_STYLE_PROP.test(prop);
    })
    .join('; ');
}

function flattenColorValue(value: string | null | undefined, extraAlpha = 1): string | null {
  if (!value) return null;
  const rgb = parseCssColorToRgb(value);
  if (!rgb) return null;
  const alpha = Number.isFinite(extraAlpha) ? extraAlpha : 1;
  return formatHexColor(rgbToHex(compositeOnWhite(rgb, alpha)));
}

function flattenPaintAttribute(element: Element, attribute: 'fill' | 'stroke', extraAlpha: number): void {
  const attrValue = element.getAttribute(attribute);
  const styleValue = readStyleProperty(element.getAttribute('style') ?? '', attribute);
  const raw = attrValue ?? styleValue;
  if (!raw || raw === 'none' || raw === 'transparent' || raw.includes('url(')) return;

  const flattened = flattenColorValue(raw, extraAlpha);
  if (!flattened) return;
  setElementPaintColor(element, attribute, flattened);
}

function flattenStopElement(stop: Element): void {
  const style = stop.getAttribute('style') ?? '';
  const stopColor =
    stop.getAttribute('stop-color') || readStyleProperty(style, 'stop-color') || '#000000';
  const stopOpacityRaw =
    stop.getAttribute('stop-opacity') || readStyleProperty(style, 'stop-opacity') || '1';
  const stopOpacity = parseFloat(stopOpacityRaw);
  const alpha = Number.isFinite(stopOpacity) ? stopOpacity : 1;

  const flattened = flattenColorValue(stopColor, alpha);
  if (flattened) stop.setAttribute('stop-color', flattened);
}

function stripOpacityPresentationFromElement(element: Element): void {
  OPACITY_ATTRS.forEach((name) => element.removeAttribute(name));

  const style = element.getAttribute('style');
  if (!style) return;
  const cleaned = cleanOpacityFromStyle(style);
  if (cleaned) element.setAttribute('style', cleaned);
  else element.removeAttribute('style');
}

export function stripOpacityPresentationFromTree(root: Element): void {
  // Flatten gradient stops before stripping stop-opacity attributes.
  root.querySelectorAll('stop').forEach((stop) => flattenStopElement(stop));
  stripOpacityPresentationFromElement(root);
  root.querySelectorAll('*').forEach((node) => stripOpacityPresentationFromElement(node));
}

type OpaquePaintContext = {
  root: SVGSVGElement;
  isGeometry: (element: Element) => boolean;
  isMeasurable: (element: Element) => boolean;
  isVisible: (computed: CSSStyleDeclaration, element: Element) => boolean;
  hasPaintedFill: (computed: CSSStyleDeclaration, element: Element) => boolean;
  hasPaintedStroke: (computed: CSSStyleDeclaration, element: Element) => boolean;
  resolveFillColor: (computed: CSSStyleDeclaration) => string | null;
  resolveStrokeColor: (computed: CSSStyleDeclaration) => string | null;
  removeStroke: (element: Element) => void;
};

/** Bake inherited/computed transparency into opaque fill/stroke colors. */
function flattenGeometryPaintsToOpaque(context: OpaquePaintContext): void {
  const { root } = context;

  Array.from(root.querySelectorAll('*'))
    .filter(context.isGeometry)
    .forEach((element) => {
      if (!context.isMeasurable(element)) return;

      const computed = getComputedStyle(element);
      if (!context.isVisible(computed, element)) return;

      const effectiveOpacity = parseFloat(computed.opacity);
      const alpha = Number.isFinite(effectiveOpacity) ? effectiveOpacity : 1;

      if (alpha <= 0) {
        element.setAttribute('fill', 'none');
        context.removeStroke(element);
        return;
      }

      if (context.hasPaintedFill(computed, element)) {
        const fillColor = context.resolveFillColor(computed);
        if (fillColor) {
          const flattened = flattenColorValue(fillColor, alpha);
          if (flattened) setElementPaintColor(element, 'fill', flattened);
        } else {
          flattenPaintAttribute(element, 'fill', alpha);
        }
      }

      if (context.hasPaintedStroke(computed, element)) {
        const strokeColor = context.resolveStrokeColor(computed);
        if (strokeColor) {
          const flattened = flattenColorValue(strokeColor, alpha);
          if (flattened) setElementPaintColor(element, 'stroke', flattened);
        } else {
          flattenPaintAttribute(element, 'stroke', alpha);
        }
      }
    });
}

export function makeSvgFullyOpaque(context: OpaquePaintContext): void {
  flattenGeometryPaintsToOpaque(context);
  stripOpacityPresentationFromTree(context.root);
}
